  import { useRef, useState } from "react";
  import axios from "axios";
  import {
    ArrowRight,
    Check,
    GraduationCap,
  } from "lucide-react";
  import { toast } from "sonner";
  import Reveal from "@/components/Reveal";
  import LottiePlayer from "@/components/LottiePlayer";
  import riseAnim from "@/assets/lottie/launch-rise.json";
  import GrowthChartBg from "@/components/GrowthChartBg";
  import { trackEvent } from "@/hooks/useAnalytics";
  import { COUNTRY_CODES, CONTACT } from "@/lib/constants";

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const initialState = {
    name: "",
    email: "",
    country_code: "+91",
    phone: "",
    college: "",
    graduation_year: "",

    role: "",

    degree: "",
    city: "",

    current_status: "",

    linkedin: "",

    resume_link: "",

    why: "",
  };

  export default function TalentTraining() {
    const [form, setForm] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const ref = useRef(null);

    const update = (k) => (e) => {
      setForm((s) => ({ ...s, [k]: e.target.value }));
      if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
    };

    const onPhone = (e) => {
      const v = e.target.value.replace(/[^\d]/g, "").slice(0, 15);
      setForm((s) => ({ ...s, phone: v }));
      if (errors.phone) setErrors((s) => ({ ...s, phone: undefined }));
    };

    const validate = () => {
      const e = {};
      if (!form.name.trim()) e.name = "Please enter your name.";
      if (!form.email.trim()) e.email = "Email is required.";
      else if (!EMAIL_RE.test(form.email)) e.email = "Enter a valid email address.";
      if (!form.phone.trim()) e.phone = "Phone is required.";
      else if (form.phone.length < 6) e.phone = "Enter a valid phone number.";
      if (!form.role)
        e.role = "Please select a training program.";

      if (!form.current_status)
        e.current_status = "Select your current status.";

      if (!form.degree.trim())
        e.degree = "Please enter your qualification.";

      if (!form.city.trim())
        e.city = "Please enter your city.";
      if (!form.why.trim()) e.why = "Tell us why you want to join.";
      return e;
    };

    const submit = async (ev) => {
      ev.preventDefault();
      const e = validate();
      setErrors(e);
      if (Object.keys(e).length) {
        toast.error("Please fix the highlighted fields.");
        const first = ref.current?.querySelector("[data-error='true']");
        if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setSubmitting(true);
      try {
        await axios.post(`${API}/training-program`, {
          ...form,

          phone: `${form.country_code} ${form.phone}`,

          source: "Website",

          page: window.location.href,

        });

        trackEvent("training_program_enquiry", { label: form.role, section: "training-form" });
        setSubmitted(true);
        setForm(initialState);
        toast.success("Enrollment request submitted successfully.");
      } catch (err) {
        console.error(err);
        toast.error(`Something went wrong. Please email ${CONTACT.email}`);
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div data-testid="page-talent-training">
        <section className="relative overflow-hidden bg-gradient-to-br from-[#F8FBFF] via-white to-[#F4FFF8] py-24 md:py-32">
    <GrowthChartBg variant="light" />

    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[var(--hop-green)]/10 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-blue-100 blur-3xl"></div>
    </div>

    <div className="hop-container relative z-10">
      <div className="hidden xl:block absolute right-0 top-4 w-[320px] opacity-90" aria-hidden="true"><LottiePlayer data={riseAnim} /></div>
      <Reveal>
        <span className="inline-flex items-center rounded-full border border-[var(--hop-green)]/20 bg-[var(--hop-green)]/10 px-4 py-2 text-sm font-semibold text-[var(--hop-green)]">
          House of Projects Talent Training Program
        </span>
      </Reveal>

      <Reveal delay={100}>
        <h1 className="mt-8 max-w-5xl font-display text-5xl font-bold leading-tight tracking-tight text-hop-blue md:text-7xl">
          Become{" "}
          <span className="text-gradient-bg">
            Interview Ready.
          </span>
          <br />
          Become Industry Ready.
          <br />
          Get Hired.
        </h1>
      </Reveal>

      <Reveal delay={200}>
        <p className="mt-8 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">
          A live, founder-led Talent Training Program designed for
          graduates, freshers and final-year students who want to
          confidently crack interviews, improve their resume, master AI
          tools and become workplace ready.
        </p>
      </Reveal>

      <Reveal delay={300}>
        <div className="mt-10 flex flex-wrap gap-4">
          <button
            type="button"
            onClick={() =>
              document
                .getElementById("careers-form")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-primary btn-emerald"
          >
            Apply Now
            <ArrowRight className="arrow h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() =>
              document
                .getElementById("training-programs")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="rounded-xl border border-slate-300 px-6 py-3 font-semibold text-hop-blue transition hover:border-[var(--hop-green)] hover:text-[var(--hop-green)]"
          >
            View Programs
          </button>
        </div>
      </Reveal>
      </div>
  </section>

        {/* =========================
        TALENT TRAINING PROGRAMS
  ========================= */}

  <section
    id="training-programs"
    className="py-24 bg-[var(--hop-grey-50)]"
  >
    <div className="hop-container">

      <Reveal>
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] text-[var(--hop-green)] font-semibold">
            Talent Training Programs
          </span>

          <h2 className="mt-4 font-display text-5xl font-bold text-hop-blue">
            Choose Your Career Launch Plan
          </h2>

          <p className="mt-6 text-lg leading-8 text-slate-600">
            Learn directly from founders and operators who've built
            businesses, hired teams and interviewed hundreds of candidates.
            Every batch is limited to only five students for personalized
            mentoring.
          </p>
        </div>
      </Reveal>

      <div className="grid lg:grid-cols-3 gap-8 mt-20">

        {/* ================= PLAN 1 ================= */}

        <Reveal delay={100}>
          <div className="hop-card rounded-3xl p-10 h-full flex flex-col">

            <span className="uppercase text-sm font-semibold text-[var(--hop-green)]">
              Starter
            </span>

            <h3 className="font-display text-3xl font-bold text-hop-blue mt-3">
              Interview Ready
            </h3>

            <div className="mt-6">
              <span className="text-5xl font-bold text-hop-blue">
                ₹5,000
              </span>
            </div>

            <p className="mt-6 text-slate-600">
              Perfect for freshers preparing for internships,
              placements and entry-level interviews.
            </p>

            <ul className="mt-8 space-y-4 flex-1">

              {[
                "1 Week Live Training",
                "Maximum 5 Students",
                "Soft Skill Coaching",
                "Interview Communication",
                "Resume Screening Support",
                "ATS Friendly Resume Tips",
                "Job Search Strategy",
                "How to Apply Effectively",
                "Dedicated Mock Interview",
                "Founder Mentorship"
              ].map((item)=>(
                <li key={item} className="flex gap-3">
                  <Check className="w-5 h-5 text-[var(--hop-green)] mt-1"/>
                  <span>{item}</span>
                </li>
              ))}

            </ul>

            <button
              type="button"
              onClick={()=>{
                setForm((s)=>({
                  ...s,
                  role:"Interview Ready Program (₹5,000)"
                }));

                document.getElementById("careers-form")
                ?.scrollIntoView({behavior:"smooth"});
              }}
              className="btn-primary btn-emerald w-full justify-center mt-10"
            >
              Apply Now
              <ArrowRight className="arrow w-4 h-4"/>
            </button>

          </div>
        </Reveal>

        {/* ================= PLAN 2 ================= */}

        <Reveal delay={200}>
          <div className="hop-card rounded-3xl border-2 border-[var(--hop-green)] p-10 h-full flex flex-col relative">

            <div className="absolute -top-4 right-6 bg-[var(--hop-green)] text-white rounded-full px-4 py-1 text-xs uppercase font-bold">
              Most Popular
            </div>

            <span className="uppercase text-sm font-semibold text-[var(--hop-green)]">
              Professional
            </span>

            <h3 className="font-display text-3xl font-bold text-hop-blue mt-3">
              Career Accelerator
            </h3>

            <div className="mt-6">
              <span className="text-5xl font-bold text-hop-blue">
                ₹10,000
              </span>
            </div>

            <p className="mt-6 text-slate-600">
              Everything included in Interview Ready plus practical
              AI skills and certifications.
            </p>

            <ul className="mt-8 space-y-4 flex-1">

              {[
                "Everything in Interview Ready",
                "5 Industry Certifications",
                "Complete Resume Improvement",
                "LinkedIn Optimization",
                "ChatGPT Training",
                "Gemini AI Training",
                "AI Prompt Engineering",
                "Research using AI",
                "Excel for Corporate Jobs",
                "Workplace Productivity Tools",
                "Advanced Mock Interviews"
              ].map((item)=>(
                <li key={item} className="flex gap-3">
                  <Check className="w-5 h-5 text-[var(--hop-green)] mt-1"/>
                  <span>{item}</span>
                </li>
              ))}

            </ul>

            <button
              type="button"
              onClick={()=>{
                setForm((s)=>({
                  ...s,
                  role:"Career Accelerator Program (₹10,000)"
                }));

                document.getElementById("careers-form")
                ?.scrollIntoView({behavior:"smooth"});
              }}
              className="btn-primary btn-emerald w-full justify-center mt-10"
            >
              Apply Now
              <ArrowRight className="arrow w-4 h-4"/>
            </button>

          </div>
        </Reveal>

        {/* ================= PLAN 3 ================= */}

        <Reveal delay={300}>
          <div className="hop-card rounded-3xl p-10 h-full flex flex-col">

            <span className="uppercase text-sm font-semibold text-[var(--hop-green)]">
              Premium
            </span>

            <h3 className="font-display text-3xl font-bold text-hop-blue mt-3">
              Career Support+
            </h3>

            <div className="mt-6">
              <span className="text-5xl font-bold text-hop-blue">
                ₹15,000
              </span>
            </div>

            <p className="mt-6 text-slate-600">
              Long-term career support even after you get placed.
            </p>

            <ul className="mt-8 space-y-4 flex-1">

              {[
                "Everything in Career Accelerator",
                "3 Months Career Mentorship",
                "Office & Workplace Guidance",
                "Tool Learning Support",
                "Corporate Communication",
                "Growth Planning",
                "Networking Community",
                "Professional Peer Group",
                "Priority Founder Access",
                "Long-Term Career Guidance"
              ].map((item)=>(
                <li key={item} className="flex gap-3">
                  <Check className="w-5 h-5 text-[var(--hop-green)] mt-1"/>
                  <span>{item}</span>
                </li>
              ))}

            </ul>

            <button
              type="button"
              onClick={()=>{
                setForm((s)=>({
                  ...s,
                  role:"Career Support Plus (₹15,000)"
                }));

                document.getElementById("careers-form")
                ?.scrollIntoView({behavior:"smooth"});
              }}
              className="btn-primary btn-emerald w-full justify-center mt-10"
            >
              Apply Now
              <ArrowRight className="arrow w-4 h-4"/>
            </button>

          </div>
        </Reveal>

      </div>

    </div>

  </section>

  {/* =========================
  WHY OUR TRAINING WORKS
  ========================= */}

  <section className="py-24 bg-white">

    <div className="hop-container">

      <Reveal>
        <div className="text-center max-w-3xl mx-auto">

          <span className="text-xs uppercase tracking-[0.25em] text-[var(--hop-green)] font-semibold">
            Why House of Projects
          </span>

          <h2 className="mt-4 font-display text-5xl font-bold text-hop-blue">
            Built for Real Hiring.
            <br />
            Not Just Certificates.
          </h2>

          <p className="mt-6 text-lg leading-8 text-slate-600">
            Most training programs stop after teaching concepts. We prepare you
            to actually clear interviews, communicate confidently and perform in
            a real workplace.
          </p>

        </div>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-8 mt-20">

        {[
          {
            title:"Founder-Led Mentorship",
            desc:"Learn directly from founders who have hired candidates, built teams and worked with startups and enterprises."
          },

          {
            title:"Very Small Batches",
            desc:"Maximum 5 students in one batch so every participant gets individual attention."
          },

          {
            title:"Interview First Approach",
            desc:"Mock interviews, HR questions, communication training and resume improvement."
          },

          {
            title:"AI & Modern Workplace Skills",
            desc:"Learn ChatGPT, Gemini, AI prompting, research workflows and productivity tools."
          },

          {
            title:"Career Guidance",
            desc:"Understand where to apply, how recruiters shortlist and what companies actually expect."
          },

          {
            title:"Long-Term Support",
            desc:"Premium students continue receiving guidance even after getting placed."
          }

        ].map((item)=>(
          <Reveal key={item.title}>
            <div className="hop-card rounded-2xl p-8 h-full">

              <div className="w-12 h-12 rounded-xl bg-[var(--hop-green)]/10 flex items-center justify-center mb-6">
                <Check className="w-6 h-6 text-[var(--hop-green)]"/>
              </div>

              <h3 className="font-display text-2xl font-bold text-hop-blue">
                {item.title}
              </h3>

              <p className="mt-4 leading-7 text-slate-600">
                {item.desc}
              </p>

            </div>
          </Reveal>
        ))}

      </div>

      <Reveal delay={300}>
        <div className="mt-20 rounded-3xl bg-hop-blue text-white p-12 text-center">

          <h3 className="font-display text-4xl font-bold">
            Learn Once. Benefit Throughout Your Career.
          </h3>

          <p className="mt-6 max-w-3xl mx-auto text-lg text-white/80 leading-8">
            Whether you're preparing for your first internship, first placement,
            or your first corporate role, this program is designed to make you
            job-ready-not just course-ready.
          </p>

        </div>
      </Reveal>

    </div>

  </section>

        {/* Form */}
        <section id="careers-form" className="py-16 md:py-24 bg-white">
          <div className="hop-container max-w-3xl">
            <Reveal>
              <div className="mb-8">
                <div className="text-xs tracking-[0.22em] uppercase font-semibold text-[var(--hop-green)]">
                  Enrollment Form
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-bold text-hop-blue mt-3 tracking-tight">
                  Enroll in the Talent Training Program
                </h2>
              </div>
            </Reveal>

            {submitted ? (
              <div
                className="bg-white rounded-2xl border border-slate-200 p-10 md:p-14 shadow-[0_24px_60px_-30px_rgba(10,37,64,0.25)] text-center"
                data-testid="training-success"
              >
                <div className="w-14 h-14 rounded-full bg-[var(--hop-green)] inline-flex items-center justify-center mx-auto">
                  <Check className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-3xl font-bold text-hop-blue mt-5 tracking-tight">
                  Enrollment Request Received!

                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed max-w-md mx-auto">
                  Thank you for enrolling in the House of Projects Talent Training Program.

                  Our team will contact you within 24 working hours to discuss your selected program, upcoming batch dates and payment process.
                </p>
              </div>
            ) : (
              <Reveal>
                <form
                  ref={ref}
                  onSubmit={submit}
                  noValidate
                  className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10 shadow-[0_24px_60px_-30px_rgba(10,37,64,0.25)]"
                  data-testid="training-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div data-error={errors.name ? "true" : undefined}>
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        Full Name <span className="text-[var(--hop-green)] ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        placeholder="Enter your full name"
                        onChange={update("name")}
                        disabled={submitting}
                        className={`hop-input ${errors.name ? "!border-red-500" : ""}`}
                        data-testid="careers-input-name"
                      />
                      {errors.name && <div className="mt-1.5 text-xs text-red-600">{errors.name}</div>}
                    </div>
                    <div data-error={errors.email ? "true" : undefined}>
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        Email <span className="text-[var(--hop-green)] ml-1">*</span>
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        placeholder="name@example.com"
                        onChange={update("email")}
                        disabled={submitting}
                        className={`hop-input ${errors.email ? "!border-red-500" : ""}`}
                        data-testid="careers-input-email"
                      />
                      {errors.email && <div className="mt-1.5 text-xs text-red-600">{errors.email}</div>}
                    </div>

                    <div className="md:col-span-2" data-error={errors.phone ? "true" : undefined}>
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        Phone Number <span className="text-[var(--hop-green)] ml-1">*</span>
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={form.country_code}
                          onChange={update("country_code")}
                          className="hop-input !w-auto !pr-8"
                          data-testid="careers-input-country-code"
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.code}  {c.country}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          inputMode="numeric"
                          value={form.phone}
                          onChange={onPhone}
                          disabled={submitting}
                          placeholder="9876543210"
                          className={`hop-input flex-1 ${errors.phone ? "!border-red-500" : ""}`}
                          data-testid="careers-input-phone"
                        />
                      </div>
                      {errors.phone && <div className="mt-1.5 text-xs text-red-600">{errors.phone}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        College
                      </label>
                      <input
                        type="text"
                        value={form.college}
                        placeholder="Your College / University"
                        onChange={update("college")}
                        disabled={submitting}
                        className="hop-input"
                        data-testid="careers-input-college"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        Graduation Year
                      </label>
                      <input
                        type="text"
                        value={form.graduation_year}
                        onChange={update("graduation_year")}
                        disabled={submitting}
                        className="hop-input"
                        placeholder="e.g. 2026"
                        data-testid="careers-input-graduation"
                      />
                    </div>

                    <div>
    <label className="block text-sm font-semibold text-hop-blue mb-2">
      Qualification
      <span className="text-[var(--hop-green)] ml-1">*</span>
    </label>

    <input
      value={form.degree}
      onChange={update("degree")}
      placeholder="B.Tech, MBA, B.Com, B.Pharma..."
      className={`hop-input ${
        errors.degree ? "!border-red-500" : ""
      }`}
    />

    {errors.degree && (
      <div className="mt-1 text-xs text-red-600">
        {errors.degree}
      </div>
    )}
  </div>

  <div>
    <label className="block text-sm font-semibold text-hop-blue mb-2">
      Current City
      <span className="text-[var(--hop-green)] ml-1">*</span>
    </label>

    <input
      value={form.city}
      onChange={update("city")}
      placeholder="Delhi, Indore, Bangalore..."
      className={`hop-input ${
        errors.city ? "!border-red-500" : ""
      }`}
    />

    {errors.city && (
      <div className="mt-1 text-xs text-red-600">
        {errors.city}
      </div>
    )}
  </div>

                    <div className="md:col-span-2" data-error={errors.role ? "true" : undefined}>
    <label className="block text-sm font-semibold text-hop-blue mb-2">
      Select Your Training Program
      <span className="text-[var(--hop-green)] ml-1">*</span>
    </label>

    <select
      value={form.role}
      onChange={update("role")}
      className={`hop-input ${errors.role ? "!border-red-500" : ""}`}
      data-testid="careers-input-role"
    >
      <option value="">Choose a Program</option>

      <option value="Interview Ready Program (₹5,000)">
        Interview Ready Program (₹5,000)
      </option>

      <option value="Career Accelerator Program (₹10,000)">
        Career Accelerator Program (₹10,000)
      </option>

      <option value="Career Support Plus (₹15,000)">
        Career Support Plus (₹15,000)
      </option>
    </select>

    {errors.role && (
      <div className="mt-1.5 text-xs text-red-600">
        {errors.role}
      </div>
    )}

    <p className="mt-2 text-xs text-slate-500">
      Selecting a program here helps us prepare your onboarding and batch allocation.
    </p>
  </div>

  <div
    className="md:col-span-2"
    data-error={errors.current_status ? "true" : undefined}
  >
    <label className="block text-sm font-semibold text-hop-blue mb-2">
      Current Status
      <span className="text-[var(--hop-green)] ml-1">*</span>
    </label>

    <select
      value={form.current_status}
      onChange={update("current_status")}
      className={`hop-input ${errors.current_status ? "!border-red-500" : ""}`}
    >
      <option value="">Select Current Status</option>

      <option value="Student">Student</option>

      <option value="Final Year Student">Final Year Student</option>

      <option value="Graduate">Graduate</option>

      <option value="Working Professional">Working Professional</option>

      <option value="Career Switch">Career Switch</option>
    </select>

    {errors.current_status && (
      <div className="mt-1.5 text-xs text-red-600">
        {errors.current_status}
      </div>
    )}
  </div>
                    <div>
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        LinkedIn Profile
                      </label>
                      <input
                        type="url"
                        value={form.linkedin}
                        onChange={update("linkedin")}
                        placeholder="https://linkedin.com/in/…"
                        className="hop-input"
                        data-testid="careers-input-linkedin"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        Resume / CV Link (Drive)
                      </label>
                      <input
                        type="url"
                        value={form.resume_link}
                        onChange={update("resume_link")}
                        placeholder="Google Drive, Dropbox or any shareable link"
                        className="hop-input"
                        data-testid="careers-input-resume"
                      />
                      <div className="mt-1.5 text-xs text-slate-500">
                        Please upload your resume to Google Drive, enable "Anyone with the link can view", and paste the link here. Or email it to {CONTACT.email}
                      </div>
                    </div>

                    <div className="md:col-span-2" data-error={errors.why ? "true" : undefined}>
                      <label className="block text-sm font-semibold text-hop-blue mb-2">
                        Tell us about your career goals <span className="text-[var(--hop-green)] ml-1">*</span>
                      </label>
                      <textarea
    placeholder="Example: I'm a final year B.Tech student looking to secure an internship in Product Management. I want to improve my resume, communication and interview confidence."
                        rows={5}
                        value={form.why}
                        onChange={update("why")}
                        className={`hop-input ${errors.why ? "!border-red-500" : ""}`}
                        data-testid="careers-input-why"
                      />
                      {errors.why && <div className="mt-1.5 text-xs text-red-600">{errors.why}</div>}
                    </div>
                  </div>

                  <div className="mt-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-xs text-slate-500 max-w-sm flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-[var(--hop-green)]" />
                      Limited to only 5 students per batch • Founder-led Live Sessions • Personalized Mentorship
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary btn-emerald justify-center"
                      data-testid="careers-form-submit"
                    >
                      {submitting ? "Submitting your enrollment..." : "Request Enrollment"}
                      <ArrowRight className="arrow w-4 h-4" />
                    </button>
                  </div>
                </form>
              </Reveal>
            )}
          </div>
        </section>
      </div>
    );
  }
